import React, { Fragment, useState, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { useStores } from '../../hooks/use-stores';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import {
    Block,
    SpaceBetweenDiv,
    BigModal,
    ThemedButton,
    Heading2,
    TextDefault,
} from '@awarego-ui/components';
import AddManager from '../../components/super/add-manager';
import AddManagerDef from '../../forms/add-manager';
import { useLocalObservable } from 'mobx-react';
import TableClient from '../../components/table/TableClient';
import { CircularProgress, Menu, MenuItem } from '@mui/material';
import { DarkArrowTooltipIcon } from '@awarego-ui/components';
import { extractErrorMessage } from '../../utils/helpers';
import PartnerDistributorBackNav from './PartnerDistributorBackNav';

function PartnerAdmins() {
    const { partnerId } = useParams();
    const { commonStore, authStore, partnerStore } = useStores();
    const [showAddManagerModal, setShowAddManagerModal] = useState(false);
    const [addManagerError, setAddManagerError] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [targetAdmin, setTargetAdmin] = useState(null);

    const {
        partnerManagers: managers,
        loadingPartnerManagers: loadingManagers,
        addingPartnerManager: addingManager,
        removingPartnerManagers,
        currentPartner,
    } = partnerStore;

    const partnerName = currentPartner?.name || 'Partner';

    const localStore = useLocalObservable(() => {
        let managerForm = new AddManagerDef(
            { values: { role: 1 } },
            {
                onSuccess() {
                    doAddManager(this);
                },
            }
        );
        return { managerForm };
    });

    useEffect(() => {
        void partnerStore.loadPartnerManagers(partnerId);
        void partnerStore.loadSettings(partnerId);
    }, [partnerId]);

    const doAddManager = async (form) => {
        setAddManagerError(null);
        try {
            await partnerStore.addPartnerManager(partnerId, form.values());
            setShowAddManagerModal(false);
            commonStore.success('Admin added!');
        } catch (error) {
            const msg = extractErrorMessage(error);
            setAddManagerError(msg);
            commonStore.error(msg);
        }
    };

    const doRemoveManager = (userId, name, email) => {
        commonStore.showConfirm(
            `Admin ${name || email} will be removed from ${partnerName}`,
            'Remove',
            `Remove ${name || email}?`,
            async () => {
                try {
                    await partnerStore.removePartnerManager(userId, partnerId);
                    commonStore.success('Admin removed!');
                } catch (error) {
                    commonStore.error(extractErrorMessage(error));
                }
            },
            true
        );
    };

    const doSendLoginInfo = (managerEmail) => {
        commonStore.showConfirm(
            `Do you want to send an email to ${managerEmail} with a login link?`,
            'Send email',
            'Send login email',
            async () => {
                try {
                    await authStore.signin(
                        managerEmail,
                        null,
                        null,
                        null,
                        `/partner/${partnerId}/companies`,
                        'login-info'
                    );
                    commonStore.success('Login email sent!');
                } catch (error) {
                    commonStore.error(extractErrorMessage(error));
                }
            },
            false
        );
    };

    const handleMenuOpen = (e, admin) => {
        setTargetAdmin(admin);
        setAnchorEl(e.currentTarget);
    };

    const handleMenuClose = () => {
        setTargetAdmin(null);
        setAnchorEl(null);
    };

    const columns = useMemo(
        () => [
            {
                Header: 'Email',
                accessor: 'email',
            },
            {
                Header: 'Name',
                accessor: 'name',
            },
            {
                Header: ' ',
                Cell: (x) => buildExtraActionsColumn(x.row.original),
            },
        ],
        [partnerName, removingPartnerManagers.length]
    );

    const buildExtraActionsColumn = (row) => {
        const isRemoving = removingPartnerManagers.includes(row.user_id || row.id);
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                <div style={{ flex: '0 0 40px', display: 'flex', alignItems: 'center'}}>
                    {isRemoving ? (
                        <CircularProgress size={20} style={{ margin: 0 }} />
                    ) : (
                        <DarkArrowTooltipIcon
                            customFunction={() => {
                                doRemoveManager(row.user_id || row.id, row.name, row.email);
                            }}
                            row={row}
                            title="Delete Admin"
                            icon={'delete'}
                        />
                    )}
                </div>
                <DarkArrowTooltipIcon
                    customFunction={(e) => handleMenuOpen(e, row)}
                    row={row}
                    title="More"
                    icon={'more'}
                />
            </div>
        );
    };

    if (loadingManagers) {
        return <ThemedSpinner />;
    }

    return (
        <Fragment>
            <PartnerDistributorBackNav
                partnerId={partnerId}
                sectionLabel="Admins"
            />
            <Block>
                <SpaceBetweenDiv ml={16} pr={32}>
                    <div>
                        <Heading2 displayBlock>Admins</Heading2>
                        <TextDefault lighter displayBlock>
                            Manage {partnerName} administrators
                        </TextDefault>
                    </div>
                    <ThemedButton
                        mode="secondary"
                        text="Add admin"
                        onClick={() => {
                            localStore.managerForm.reset();
                            // role field is hidden for partner admins — reset to default
                            // so required|numeric validation doesn't silently block submit
                            localStore.managerForm.$('role').set('value', 1);
                            setShowAddManagerModal(true);
                            setAddManagerError(null);
                        }}
                    />
                </SpaceBetweenDiv>
                {managers.length > 0 ? (
                    <TableClient
                        columns={columns}
                        data={managers}
                        hidePagination={managers.length < 10}
                        initialPageSize={10}
                    />
                ) : (
                    <TextDefault lighter ml={16} mt={16}>
                        No admins found. Add your first admin to get started.
                    </TextDefault>
                )}
            </Block>
            <BigModal
                isOpen={showAddManagerModal}
                onRequestClose={() => {
                    setShowAddManagerModal(false);
                    setAddManagerError(null);
                }}
                heading="Add new admin"
                modalContent={
                    <AddManager
                        error={addManagerError}
                        managerForm={localStore.managerForm}
                        addManagerError={addManagerError}
                        hideRole
                    />
                }
                modalFooterContent={
                    addingManager ? (
                        <ThemedButton
                            mode="primary"
                            type="button"
                            disabled
                            $saving={true}
                            text="Saving"
                            $center={true}
                        />
                    ) : (
                        <Fragment>
                            <ThemedButton
                                mode="secondary"
                                text="Cancel"
                                onClick={() => {
                                    setShowAddManagerModal(false);
                                    setAddManagerError(null);
                                }}
                            />
                            <ThemedButton
                                mode="primary"
                                text="Add"
                                onClick={() => {
                                    localStore.managerForm.submit();
                                }}
                            />
                        </Fragment>
                    )
                }
            />
            {targetAdmin && (
                <Menu
                    id="simple-menu"
                    anchorEl={anchorEl}
                    keepMounted
                    open={targetAdmin != null}
                    onClose={handleMenuClose}
                    elevation={1}
                >
                    <MenuItem
                        onClick={(e) => {
                            doSendLoginInfo(targetAdmin.email);
                            handleMenuClose(e);
                        }}
                    >
                        Send login link
                    </MenuItem>
                </Menu>
            )}
        </Fragment>
    );
}

export default observer(PartnerAdmins);
